import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { wrapFunctionalComponent } from 'utils/framework';
import Debounce from 'utils/Debounce';

import { Button, Grid } from 'components/ui';
import Modal from 'components/Modal';
import ErrorList from 'components/ErrorList';
import AddressList from 'components/RichProfile/MyAccount/AutoReplenishment/ManageAddress/SavedAddressesModal/components/AddressList';

import { savedAddressesModalSelector } from 'components/RichProfile/MyAccount/AutoReplenishment/ManageAddress/SavedAddressesModal/savedAddressesModalSelector';

function SavedAddressesModal({
    errorMessages = null,
    initialSelectedId = '',
    isOpen = false,
    onAddressAdd = null,
    onAddressEdit = null,
    onAddressRemove = null,
    onAddressSubmit = null,
    onDismiss = null,
    savedAddresses = []
}) {
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(initialSelectedId);

    const { title, cancelButtonText, confirmButtonText } = useSelector(savedAddressesModalSelector);

    useEffect(() => {
        if (isOpen) {
            setReorderedAddresses();
        }
    }, [isOpen]);

    useEffect(() => {
        if (initialSelectedId) {
            setSelectedAddressId(initialSelectedId);
        }
    }, [initialSelectedId]);

    const reorderAddresses = useCallback(
        addressList => {
            if (!initialSelectedId || addressList.length < 2) {
                return addressList;
            }

            const selectedAddress = addressList.find(addr => addr.addressId === initialSelectedId);

            // If address not found or is the default, no reordering needed
            if (!selectedAddress || selectedAddress.isDefault) {
                return addressList;
            }

            // Check if already in second position
            if (addressList[1]?.addressId === initialSelectedId) {
                return addressList;
            }

            // Reorder: [default, initialSelected, ...rest]
            const defaultAddress = addressList.find(addr => addr.isDefault);
            const otherAddresses = addressList.filter(addr => addr.addressId !== initialSelectedId && !addr.isDefault);

            if (defaultAddress) {
                return [defaultAddress, selectedAddress, ...otherAddresses];
            }

            // If no default, put selected first
            return [selectedAddress, ...otherAddresses];
        },
        [initialSelectedId]
    );

    /**
     * Re-order savedAddresses
     */
    const setReorderedAddresses = useCallback(() => {
        const orderedList = reorderAddresses(savedAddresses);

        setAddresses(orderedList);

        // Auto-select first address if none selected
        if (!selectedAddressId && orderedList.length > 0) {
            setSelectedAddressId(orderedList[0].addressId);
        }
    }, [savedAddresses, selectedAddressId, reorderAddresses]);

    /**
     * Handle address selection
     */
    const handleAddressSelect = useCallback(addressId => {
        setSelectedAddressId(addressId);
    }, []);

    /**
     * Handle confirm/use selected address
     */
    const handleConfirm = useCallback(() => {
        if (!selectedAddressId) {
            return;
        }

        const selectedAddress = addresses.find(addr => addr.addressId === selectedAddressId);

        if (selectedAddress) {
            onAddressSubmit?.(selectedAddress);
        }
    }, [selectedAddressId, addresses, onAddressSubmit]);

    /**
     * Debounced confirm
     */
    const handleConfirmDebounced = Debounce.preventDoubleClick(handleConfirm);

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            hasBodyScroll={true}
            width={0}
            focusDialog={true}
            isDrawer={true}
        >
            <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <ErrorList errorMessages={errorMessages} />
                <AddressList
                    addresses={addresses}
                    selectedAddressId={selectedAddressId}
                    onAddressAdd={onAddressAdd}
                    onAddressSelect={handleAddressSelect}
                    onAddressEdit={onAddressEdit}
                    onAddressRemove={onAddressRemove}
                />
            </Modal.Body>

            <Modal.Footer hasBorder={true}>
                <Grid
                    gap={4}
                    gridTemplateColumns={['1fr 1fr']}
                >
                    <Button
                        variant='secondary'
                        block={true}
                        onClick={onDismiss}
                        data-at={Sephora.debug.dataAt('address_list_cancel')}
                    >
                        {cancelButtonText}
                    </Button>
                    <Button
                        variant='primary'
                        block={true}
                        onClick={handleConfirmDebounced}
                        disabled={selectedAddressId === initialSelectedId}
                        data-at={Sephora.debug.dataAt('address_list_confirm')}
                    >
                        {confirmButtonText}
                    </Button>
                </Grid>
            </Modal.Footer>
        </Modal>
    );
}

export default wrapFunctionalComponent(SavedAddressesModal, 'SavedAddressesModal');
